/**
 * Attentive Back-in-Stock Email Opt-in
 * =====================================
 * Handles "Notify me when back in stock" submissions and sends the customer's
 * email + product/variant data to Attentive via a server-side proxy (Vercel).
 *
 * CONFIGURATION  (in custom.js)
 * ------------------------------------
 *   window.attentiveBackInStockConfig = {
 *     signUpSourceId: '',
 *     webhookUrl: 'https://back-in-stock-notification.vercel.app/api/back-in-stock',
 *   };
 */

(function () {
  'use strict';

  var SDK_WAIT_MS = 5000;
  var SDK_POLL_MS = 100;

  function findSDK() {
    return window.__attentive || window.attentive || window.attentive_tag || null;
  }

  function isUsableSDK(sdk) {
    return sdk && (
      typeof sdk.productAlertSignUp === 'function' ||
      typeof sdk.subscribe === 'function' ||
      typeof sdk.push === 'function'
    );
  }

  function waitForSDK() {
    return new Promise(function (resolve, reject) {
      var immediateSDK = findSDK();
      if (isUsableSDK(immediateSDK)) { resolve(immediateSDK); return; }
      var elapsed = 0;
      var timer = setInterval(function () {
        elapsed += SDK_POLL_MS;
        var sdk = findSDK();
        if (isUsableSDK(sdk)) {
          clearInterval(timer);
          resolve(sdk);
        } else if (elapsed >= SDK_WAIT_MS) {
          clearInterval(timer);
          console.warn('[Attentive BIS] SDK not usable after ' + SDK_WAIT_MS + 'ms — falling back to webhook.');
          reject(new Error('SDK_NOT_FOUND'));
        }
      }, SDK_POLL_MS);
    });
  }

  if (customElements.get('attentive-backinstock')) return;

  customElements.define(
    'attentive-backinstock',
    class AttentiveBackInStock extends HTMLElement {
      constructor() {
        super();
        this.form        = null;
        this.emailInput  = null;
        this.submitBtn   = null;
        this.successEl   = null;
        this.errorEl     = null;
        this.formWrapper     = null;
        this.subtextEl       = null;
        this.alreadyOptedEl  = null;
        this._variantChangeUnsubscribe = null;
      }

      connectedCallback() {
        this.form        = this.querySelector('[data-backinstock-form]');
        this.emailInput  = this.querySelector('[data-backinstock-email]');
        this.submitBtn   = this.querySelector('[data-backinstock-submit]');
        this.successEl   = this.querySelector('[data-backinstock-success]');
        this.errorEl     = this.querySelector('[data-backinstock-error]');
        this.formWrapper    = this.querySelector('[data-backinstock-form-wrapper]');
        this.subtextEl      = this.querySelector('.bis-subtext');
        this.alreadyOptedEl = this.querySelector('[data-backinstock-already-opted]');
        if (this.form) {
          this.form.addEventListener('submit', this._handleSubmit.bind(this));
        }
        this._checkSession();
        this._subscribeToVariantChanges();
      }

      disconnectedCallback() {
        if (this._variantChangeUnsubscribe) {
          this._variantChangeUnsubscribe();
          this._variantChangeUnsubscribe = null;
        }
      }

      _subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || !window.theme || !window.theme.PUB_SUB_EVENTS) return;
        var self = this;
        var productInfoEl = this.closest('product-info');
        var sectionId = productInfoEl ? productInfoEl.dataset.section : null;

        this._variantChangeUnsubscribe = subscribe(theme.PUB_SUB_EVENTS.variantChange, function (event) {
          var data = event && event.data;
          if (!data || !data.variant) return;
          if (sectionId && data.sectionId !== sectionId) return;

          var variant = data.variant;
          self.dataset.variantId        = variant.id;
          self.dataset.variantTitle     = variant.title;
          if (variant.inventory_item_id) self.dataset.inventoryItemId = variant.inventory_item_id;

          var variantImage = variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src
            || variant.featured_image && variant.featured_image.src || '';
          if (variantImage) self.dataset.productImage = variantImage;

          var titleEl = self.querySelector('[data-variant-title]');
          if (titleEl) {
            if (variant.title === 'Default Title') {
              titleEl.textContent = '';
              titleEl.style.display = 'none';
            } else {
              titleEl.textContent = variant.title;
              titleEl.style.display = '';
            }
          }

          // If this variant is now available, clear the opt-in session state so the
          // form shows fresh if the product sells out again in the same browser session.
          if (variant.available && window.sessionStorage) {
            try { window.sessionStorage.removeItem('bis_' + variant.id); } catch (_) {}
          }

          self._resetForm();
        });
      }

      _resetForm() {
        if (this.formWrapper)    this.formWrapper.classList.remove('is-hidden');
        if (this.subtextEl)      this.subtextEl.style.display = '';
        if (this.successEl)      { this.successEl.classList.add('is-hidden'); this.successEl.style.display = ''; }
        if (this.alreadyOptedEl) { this.alreadyOptedEl.classList.add('is-hidden'); this.alreadyOptedEl.style.display = ''; }
        if (this.emailInput)     this.emailInput.value = '';
        this._clearError();
        this._checkSession();
      }

      _handleSubmit(event) {
        event.preventDefault();
        var email = (this.emailInput && this.emailInput.value || '').trim();
        if (!email || !this._validEmail(email)) {
          this._showError('Please enter a valid email address.');
          return;
        }
        var cfg = window.attentiveBackInStockConfig || {};
        var self = this;
        var variantId = this.dataset.variantId;
        this._setLoading(true);
        this._clearError();

        var payload = {
          email:        email,
          productId:    self.dataset.productId,
          productTitle: self.dataset.productTitle,
          variantId:    variantId,
          variantTitle: self.dataset.variantTitle,
          productUrl:   self.dataset.productUrl || window.location.href,
          productImage: self.dataset.productImage || '',
          type:         'back_in_stock',
          shop:         (window.Shopify && window.Shopify.shop) || '',
        };

        var sdkTimeout = new Promise(function (_, reject) {
          setTimeout(function () { reject(new Error('SDK_TIMEOUT')); }, 4000);
        });

        waitForSDK()
          .then(function (sdk) { return Promise.race([self._subscribeViaSDK(sdk, payload, cfg), sdkTimeout]); })
          .then(function () {
            self._setLoading(false);
            self._showSuccess(payload.variantId, payload.email);
          })
          .catch(function (err) {
            console.warn('[Attentive BIS] SDK path failed:', err && err.message);
            if (cfg.webhookUrl) {
              self._callWebhook(payload, cfg.webhookUrl);
            } else {
              console.error('[Attentive BIS] No webhookUrl in config — cannot fall back. Full config:', JSON.stringify(window.attentiveBackInStockConfig || null));
              self._setLoading(false);
              self._showError(err && err.message === 'SDK_NOT_FOUND'
                ? 'Sign-up is temporarily unavailable. Please try again shortly.'
                : 'Something went wrong. Please try again.');
            }
          });
      }

      _subscribeViaSDK(sdk, payload, cfg) {
        var customAttrs = [
          { key: 'type',         value: 'back_in_stock'           },
          { key: 'productId',    value: String(payload.productId) },
          { key: 'productTitle', value: payload.productTitle      },
          { key: 'variantId',    value: String(payload.variantId) },
          { key: 'variantTitle', value: payload.variantTitle      },
        ];
        if (typeof sdk.productAlertSignUp === 'function') {
          return new Promise(function (resolve, reject) {
            try {
              sdk.productAlertSignUp({
                email: payload.email, productId: payload.productId,
                variantId: payload.variantId, signUpSourceId: cfg.signUpSourceId || '',
              }, function (err) { err ? reject(err) : resolve(); });
            } catch (e) { reject(e); }
          });
        }
        if (typeof sdk.subscribe === 'function') {
          return new Promise(function (resolve, reject) {
            try {
              sdk.subscribe({
                email: payload.email, signUpSourceId: cfg.signUpSourceId || '',
                customAttributes: customAttrs,
              }, function (err) { err ? reject(err) : resolve(); });
            } catch (e) { reject(e); }
          });
        }
        if (typeof sdk.push === 'function') {
          return new Promise(function (resolve) {
            try {
              sdk.push({ type: 'subscribe', channel: 'email', email: payload.email,
                signUpSourceId: cfg.signUpSourceId || '', customAttributes: customAttrs });
            } catch (_) {}
            resolve();
          });
        }
        console.warn('[Attentive BIS] SDK found but no known subscribe method. SDK keys:', Object.keys(sdk));
        return Promise.reject(new Error('SDK_NO_METHOD'));
      }

      _callWebhook(payload, webhookUrl) {
        var self = this;
        fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            self._setLoading(false);
            self._showSuccess(payload.variantId, payload.email);
          })
          .catch(function (err) {
            console.error('[Attentive BIS] Webhook error:', err);
            self._setLoading(false);
            self._showError('Something went wrong. Please try again.');
          });
      }

      _setLoading(isLoading) {
        if (!this.submitBtn) return;
        this.submitBtn.classList.toggle('is-loading', isLoading);
        this.submitBtn.disabled = isLoading;
      }

      _showSuccess(variantId, email) {
        if (this.formWrapper)    this.formWrapper.classList.add('is-hidden');
        if (this.subtextEl)      this.subtextEl.style.display = 'none';
        if (this.alreadyOptedEl) { this.alreadyOptedEl.classList.add('is-hidden'); this.alreadyOptedEl.style.display = ''; }
        if (this.successEl)      { this.successEl.classList.remove('is-hidden'); this.successEl.style.display = 'block'; }
        if (window.sessionStorage && variantId) {
          try { window.sessionStorage.setItem('bis_' + variantId, email || '1'); } catch (_) {}
        }
      }

      _showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.remove('is-hidden');
      }

      _clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.add('is-hidden');
      }

      _checkSession() {
        var variantId = this.dataset.variantId;
        if (!variantId || !window.sessionStorage) return;
        try {
          if (window.sessionStorage.getItem('bis_' + variantId)) {
            if (this.formWrapper)    this.formWrapper.classList.add('is-hidden');
            if (this.subtextEl)      this.subtextEl.style.display = 'none';
            if (this.successEl)      { this.successEl.classList.add('is-hidden'); this.successEl.style.display = ''; }
            if (this.alreadyOptedEl) { this.alreadyOptedEl.classList.remove('is-hidden'); this.alreadyOptedEl.style.display = 'block'; }
          }
        } catch (_) {}
      }

      _validEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }
    }
  );

  function initCartBarNotificationButtons() {
    document.querySelectorAll('[data-notification-popup-button]').forEach(function (btn) {
      if (btn.dataset.bisInitialized) return;
      btn.dataset.bisInitialized = 'true';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var productInfo = btn.closest('product-info') || document.querySelector('product-info');
        var popupOpen = productInfo
          ? productInfo.querySelector('popup-component.product-soldout-notification [data-popup-open]')
          : document.querySelector('popup-component.product-soldout-notification [data-popup-open]');
        if (popupOpen) popupOpen.dispatchEvent(new Event('click'));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartBarNotificationButtons);
  } else {
    initCartBarNotificationButtons();
  }
  document.addEventListener('theme:product-info:loaded', initCartBarNotificationButtons);

})();
